<script setup>
import { computed } from 'vue'
import Icon from './ui/Icon.vue'
import { fmt, plannedCategories, tagsOf, toggleTagFilter } from '../stores'
import { openSheet, state, toggleSelected } from '../stores/state'
import { shortDay } from '../lib/dates'
import { t } from '../stores/i18n'

const props = defineProps({
  tx: { type: Object, required: true },
  showDate: { type: Boolean, default: false },
})

const isIncome = computed(() => props.tx.amount > 0)
/** A goal pretending to put money by: shown, but there is nothing to open. */
const saving = computed(() => props.tx.saving === true)
/** Something is planned for this category, so this row answers to it. */
const fulfils = computed(() => plannedCategories.value.has(props.tx.category))

const tags = computed(() => (saving.value ? [] : tagsOf(props.tx)))

/**
 * The bank has not finished with this one: the date and the wording can both
 * still change. Worth saying on the row, because it is the row you would
 * otherwise assume is final.
 */
const pending = computed(() => props.tx.settled === 0)

const focusTag = (tag) => {
  toggleTagFilter(tag.id)
  state.ui.panel = 'transactions'
}

const selecting = computed(() => state.ui.selecting && !saving.value)
const selected = computed(() => state.ui.selected.includes(props.tx.id))
const open = () => {
  if (saving.value) return
  if (selecting.value) toggleSelected(props.tx.id)
  else openSheet('transaction', { id: props.tx.id, tx: props.tx })
}

const look = computed(() => {
  if (saving.value) return { icon: 'target', class: 'bg-goal-soft text-goal' }
  if (isIncome.value) return { icon: 'in', class: 'bg-accent-soft text-accent' }
  return { icon: 'out', class: 'bg-raised text-muted' }
})
</script>

<template>
  <!-- A saving is a statement, not a thing you can act on: no hover, no press,
       nothing to open. `disabled` says so to everyone, not just the mouse. -->
  <button
    class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition"
    :data-tx="tx.id"
    :class="[selected && 'bg-accent/8', saving ? 'cursor-default' : 'hover:bg-raised/60']"
    :disabled="saving"
    :aria-pressed="selecting ? selected : undefined"
    @click="open"
  >
    <span
      v-if="selecting"
      class="grid size-5 shrink-0 place-items-center rounded-md border transition"
      :class="selected ? 'border-accent bg-accent text-[#04140d]' : 'border-line text-transparent'"
      aria-hidden="true"
    >
      <Icon name="check" :size="13" :stroke="2.6" />
    </span>
    <span class="grid size-9 shrink-0 place-items-center rounded-xl" :class="look.class">
      <Icon :name="look.icon" :size="17" />
    </span>

    <span class="min-w-0 flex-1">
      <span
        class="block truncate text-[14.5px] leading-tight font-medium"
        :class="saving && 'text-goal'"
      >
        {{ tx.description || t(saving ? 'kind.saving' : isIncome ? 'kind.income' : 'kind.expense') }}
      </span>
      <span class="mt-1 flex min-w-0 items-center gap-1.5 text-[12px] leading-none text-faint">
        <span v-if="showDate" class="shrink-0 text-muted">{{ shortDay(tx.date) }}</span>
        <span v-if="pending" class="tag shrink-0 bg-warn-soft text-warn">{{ t('row.pending') }}</span>
        <span v-if="showDate && (tx.category || saving)" aria-hidden="true">·</span>
        <span v-if="saving" class="truncate text-goal/80">
          {{ tx.opening ? t('row.alreadySaved') : t('row.setAside') }}
        </span>
        <!-- A plan is its category, so a linked row usually shares it: the link
             marks the category rather than repeating the word after it. -->
        <!-- A saving's category is its goal, already the row's own name. -->
        <span v-if="tx.category && !saving" class="inline-flex items-center gap-1 truncate">
          <Icon v-if="fulfils" name="link" :size="12" class="shrink-0 text-muted" />
          {{ tx.category }}
        </span>
        <!-- Said as loudly as "pending", and in red: a row filed nowhere counts
             towards no plan and no budget, which is worth noticing. -->
        <span v-else-if="!saving" class="tag shrink-0 bg-danger-soft text-danger">
          {{ t('row.unplanned') }}
        </span>
        <!-- Files attached to it: a paperclip and how many. -->
        <span v-if="tx.files" class="inline-flex shrink-0 items-center gap-0.5 text-muted" :title="t('attach.count', { count: tx.files })" data-files>
          <Icon name="clip" :size="12" />{{ tx.files }}
        </span>
      </span>
      <span v-if="tags.length" class="mt-1.5 flex flex-wrap items-center gap-1">
        <span
          v-for="tag in tags"
          :key="tag.id"
          :data-tag="tag.id"
          class="tag bg-raised text-muted"
          role="button"
          tabindex="0"
          @click.stop="focusTag(tag)"
          @keydown.enter.stop.prevent="focusTag(tag)"
        >
          {{ tag.name }}
        </span>
      </span>
    </span>

    <span
      class="tnum shrink-0 text-[15px] font-semibold"
      :class="saving ? 'text-goal' : isIncome ? 'text-accent' : 'text-text'"
    >
      {{ fmt(tx.amount, { signed: true }) }}
    </span>
  </button>
</template>
